import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, UserPlus } from 'lucide-react';

export default function LoginPage() {
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('login');
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'register') {
        await register(name.trim(), password);
      } else {
        await login(name.trim(), password);
      }
      navigate('/');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') setError(detail);
      else if (err.message === 'Network Error') setError('Impossible de se connecter au serveur.');
      else setError('Une erreur est survenue. Reessaie.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F0F0F] flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-sm">
        {/* Logo & Title */}
        <div className="flex flex-col items-center mb-10">
          <img src="/logo.png" alt="LA MEUTE" data-testid="login-logo" className="w-36 h-36 object-contain mb-4" />
          <h1 className="text-5xl tracking-tight text-gradient-meute" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            La Meute
          </h1>
          <p className="text-xs text-[#AAAAAA] mt-1 uppercase tracking-widest">— Polynesie —</p>
          <p className="text-[#AAAAAA] text-sm mt-3">
            {mode === 'login' ? 'Entre ton nom pour rejoindre la meute' : 'Cree ton profil'}
          </p>
        </div>

        {/* Mode Toggle */}
        <div className="mb-6">
          <div className="py-3 rounded-md text-sm font-semibold text-center bg-gradient-meute text-white">
            <Lock className="w-4 h-4 inline mr-1.5" /> Connexion
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-md text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="name" className="text-[#CCCCCC] text-sm font-medium">Ton prenom / pseudo</Label>
            <Input
              id="name" type="text" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Thomas" required autoFocus data-testid="login-name-input"
              className="bg-[#1B1B1B] border-[#2A2A2E] text-white placeholder-zinc-600 focus:border-[#FF6B35] focus:ring-[#FF6B35] text-lg h-12"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-[#CCCCCC] text-sm font-medium">Mot de passe de la meute</Label>
            <Input
              id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••" required data-testid="login-password-input"
              className="bg-[#1B1B1B] border-[#2A2A2E] text-white placeholder-zinc-600 focus:border-[#FF6B35] focus:ring-[#FF6B35] h-12"
            />
          </div>

          <Button
            type="submit" disabled={loading} data-testid="login-submit-button"
            className="w-full bg-gradient-meute text-white font-bold hover:opacity-90 text-base py-6 rounded-md"
          >
            {loading ? 'Chargement...' : mode === 'login' ? 'Entrer' : 'Rejoindre la meute'}
          </Button>
        </form>
      </div>
    </div>
  );
}
